/**
 * Reviews — réplica del backoffice de Skeleton.
 *   - 3 KPI cards: Total Reviews / Pendientes / Rating Promedio
 *   - Tabs: Todas / Pendientes / Aprobadas / Rechazadas
 *   - Lista de reviews con producto, customer, rating, comentario, fecha
 *   - Botones de moderación: Aprobar / Rechazar / Eliminar
 */
import { createFileRoute } from '@tanstack/react-router'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { useState } from 'react'
import { Card, Badge, Button, PageHeader } from '@/components/ui'
import { tenantApi } from '@/lib/api'
import { useAuth } from '@/lib/auth-store'
import { cn } from '@/lib/cn'
import { Star, MessageSquare, Check, X, Trash2 } from 'lucide-react'

export const Route = createFileRoute('/store/$id/reviews')({
  component: ReviewsPage,
})

interface Review {
  id: string
  productId: string
  customerId: string
  orderId: string
  rating: number
  title: string | null
  comment: string | null
  isVerified: boolean
  status: 'pending' | 'approved' | 'rejected'
  productName: string | null
  customerName: string
  customerEmail: string | null
  createdAt: string
}

interface ReviewsResponse {
  data: Review[]
  stats: { total: number; pending: number; approved: number; rejected: number; avgRating: number }
}

type Tab = 'all' | 'pending' | 'approved' | 'rejected'

function ReviewsPage() {
  const { activeTenantId } = useAuth()
  const [tab, setTab] = useState<Tab>('all')

  const { data, isLoading } = useQuery({
    queryKey: ['reviews', tab],
    queryFn: () => tenantApi.get<ReviewsResponse>(
      tab === 'all' ? '/api/v1/reviews' : `/api/v1/reviews?status=${tab}`,
    ),
    enabled: !!activeTenantId,
  })

  const list = data?.data ?? []
  const stats = data?.stats ?? { total: 0, pending: 0, approved: 0, rejected: 0, avgRating: 0 }

  return (
    <>
      <PageHeader title="Reviews" description="Calificaciones y comentarios de productos" />
      <div className="p-8 space-y-4">
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
          <Card className="p-5">
            <p className="text-xs text-muted-foreground uppercase tracking-wider">Total Reviews</p>
            <p className="text-3xl font-bold text-foreground tabular-nums mt-1">{stats.total}</p>
          </Card>
          <Card className="p-5">
            <p className="text-xs text-muted-foreground uppercase tracking-wider">Pendientes</p>
            <p className="text-3xl font-bold text-foreground tabular-nums mt-1">{stats.pending}</p>
          </Card>
          <Card className="p-5">
            <p className="text-xs text-muted-foreground uppercase tracking-wider">Rating Promedio</p>
            <p className="text-3xl font-bold text-foreground tabular-nums mt-1 flex items-center gap-1.5">
              {stats.avgRating.toFixed(1)}
              <Star className="size-5 fill-yellow-400 text-yellow-700 dark:text-yellow-400" />
            </p>
          </Card>
        </div>

        <div className="flex items-center gap-1.5 flex-wrap">
          {(['all', 'pending', 'approved', 'rejected'] as const).map((t) => (
            <button
              key={t}
              onClick={() => setTab(t)}
              className={cn(
                'px-3 py-1.5 text-sm rounded-md transition border',
                tab === t ? 'bg-[hsl(var(--muted))] text-foreground border-border' : 'bg-transparent text-muted-foreground border-transparent hover:text-foreground',
              )}
            >
              {t === 'all' ? 'Todas' : t === 'pending' ? 'Pendientes' : t === 'approved' ? 'Aprobadas' : 'Rechazadas'}
            </button>
          ))}
        </div>

        {isLoading && <p className="text-sm text-muted-foreground">Cargando...</p>}
        {!isLoading && list.length === 0 && (
          <Card className="p-12 flex flex-col items-center justify-center text-center">
            <MessageSquare className="size-12 text-muted-foreground mb-3" />
            <p className="text-base font-medium">No hay reviews</p>
            <p className="text-sm text-muted-foreground mt-1">
              {tab === 'pending' ? 'Sin reviews pendientes de moderar.' : 'Cuando los clientes dejen reseñas aparecerán aquí.'}
            </p>
          </Card>
        )}

        {list.length > 0 && (
          <div className="space-y-3">
            {list.map((r) => <ReviewRow key={r.id} review={r} />)}
          </div>
        )}
      </div>
    </>
  )
}

function ReviewRow({ review }: { review: Review }) {
  const qc = useQueryClient()
  const inv = () => qc.invalidateQueries({ queryKey: ['reviews'] })
  const approve = useMutation({ mutationFn: () => tenantApi.post(`/api/v1/reviews/${review.id}/approve`, {}), onSuccess: inv })
  const reject = useMutation({ mutationFn: () => tenantApi.post(`/api/v1/reviews/${review.id}/reject`, {}), onSuccess: inv })
  const remove = useMutation({ mutationFn: () => tenantApi.del(`/api/v1/reviews/${review.id}`), onSuccess: inv })

  const statusVariant = review.status === 'approved' ? 'success' : review.status === 'pending' ? 'warning' : 'destructive'
  const statusLabel = review.status === 'approved' ? 'Aprobada' : review.status === 'pending' ? 'Pendiente' : 'Rechazada'
  const date = new Date(review.createdAt).toLocaleDateString('es-CO', { day: '2-digit', month: 'short', year: 'numeric' })

  return (
    <Card className="p-5">
      <div className="flex items-start justify-between gap-4 mb-3">
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <p className="font-medium">{review.customerName}</p>
            {review.isVerified && (
              <span className="text-xs text-emerald-700 dark:text-emerald-400 flex items-center gap-1">
                <Check className="size-3" /> Compra verificada
              </span>
            )}
          </div>
          <p className="text-xs text-muted-foreground mt-0.5">
            sobre <span className="text-foreground">{review.productName ?? 'Producto'}</span> · {date}
          </p>
        </div>
        <div className="flex items-center gap-2 shrink-0">
          <div className="flex items-center gap-0.5">
            {[1, 2, 3, 4, 5].map((n) => (
              <Star
                key={n}
                className={cn('size-4', n <= review.rating ? 'fill-yellow-400 text-yellow-700 dark:text-yellow-400' : 'text-muted-foreground')}
              />
            ))}
          </div>
          <Badge variant={statusVariant}>{statusLabel}</Badge>
        </div>
      </div>

      {review.title && <p className="font-semibold text-sm mb-1">{review.title}</p>}
      {review.comment && <p className="text-sm text-foreground whitespace-pre-wrap">{review.comment}</p>}

      <div className="flex items-center gap-2 mt-4 pt-3 border-t border-border">
        {review.status !== 'approved' && (
          <Button size="sm" variant="outline" onClick={() => approve.mutate()} disabled={approve.isPending}>
            <Check className="size-4 mr-2" /> Aprobar
          </Button>
        )}
        {review.status !== 'rejected' && (
          <Button size="sm" variant="outline" onClick={() => reject.mutate()} disabled={reject.isPending}>
            <X className="size-4 mr-2" /> Rechazar
          </Button>
        )}
        <button
          onClick={() => { if (confirm('¿Eliminar esta review?')) remove.mutate() }}
          className="ml-auto p-1.5 rounded text-red-700 dark:text-red-400 hover:text-red-300 hover:bg-red-100 dark:hover:bg-red-950/40"
          title="Eliminar"
        >
          <Trash2 className="size-4" />
        </button>
      </div>
    </Card>
  )
}
